/**
 * The four programs an engagement moves through.
 *
 * These replace the ICON method's four phases, and they run in the same order,
 * so every journey the portal draws keeps its shape:
 *
 *   ICON           Identity → Communication → Optimization → Navigation
 *   price list     Strategy → Identity      → Website & Growth → Leadership
 *
 * The keys are the program ids in `catalog_programs` (migration 0071). Prices,
 * services and deliverables live in the database; what is here is only what a
 * screen needs to draw the journey without a round trip — including on pages a
 * client opens, who cannot read the price list.
 *
 * Where a screen quotes a promise made to the client, it quotes the price
 * list's own wording for that deliverable. A test holds those strings to the
 * seed, so this file cannot quietly drift from what was sold.
 */

export const PROGRAMS = [
  {
    key: 'brand-strategy',
    letter: '1',
    title: 'Brand Strategy',
    description: 'Who the firm serves, why it wins, and how the portfolio is organised.',
    accent: '#EF4237',
    legacyPhase: 'identity',
  },
  {
    key: 'brand-identity',
    letter: '2',
    title: 'Brand Identity',
    description: 'The visual and verbal system, and the tools that carry it.',
    accent: '#183C44',
    legacyPhase: 'communication',
  },
  {
    key: 'website-growth',
    letter: '3',
    title: 'Website & Growth',
    description: 'The website and the machinery that turns visitors into pipeline.',
    accent: '#5E8A91',
    legacyPhase: 'optimization',
  },
  {
    key: 'marketing-leadership',
    letter: '4',
    title: 'Marketing Leadership',
    description: 'Ongoing leadership and care after launch.',
    accent: '#8C7462',
    legacyPhase: 'navigation',
  },
]

export const PROGRAM_KEYS = PROGRAMS.map(p => p.key)

const BY_KEY = Object.fromEntries(PROGRAMS.map(p => [p.key, p]))
const BY_LEGACY = Object.fromEntries(PROGRAMS.map(p => [p.legacyPhase, p]))

/**
 * The program a project is in — read from the project, never inferred.
 *
 * `program_id` is the answer. A record that still carries only the retired
 * ICON phase is read through the same order the migration used, so history
 * keeps rendering; nothing writes `icon_phase` any more.
 *
 * Null is a real answer and the default one: a $250 email signature is not a
 * program engagement, and every surface shows nothing rather than a step it
 * cannot justify. (The same rule the ICON phase followed, for the same reason.)
 */
export function getProgram(record) {
  const id = String(record?.program_id || '').trim()
  if (id) return BY_KEY[id] || null
  const legacy = String(record?.icon_phase || '').trim().toLowerCase()
  return (legacy && BY_LEGACY[legacy]) || null
}

export function isProgramEngagement(record) {
  return getProgram(record) !== null
}

/**
 * What each program promises, for the screens that show it to a client.
 *
 * The deliverable strings are the price list's work packages, verbatim — see
 * the test that holds them to migration 0071.
 */
export const PROGRAM_SCOPES = {
  'brand-strategy': {
    subtitle: 'Positioning & Messaging',
    schedule: '2–3 weeks',
    summary: 'Who the firm serves, why it wins, and the words that prove it.',
    deliverables: [
      'Kickoff workshop (half-day) with founders and leadership',
      'Competitive landscape audit (up to 5 competitors) with positioning map',
      'Positioning statement and core value proposition',
      'Messaging hierarchy: brand promise, 3–4 pillars, proof points',
    ],
  },
  'brand-identity': {
    subtitle: 'Visual Identity & Guidelines',
    schedule: '4–6 weeks',
    summary: 'The visual system that makes the positioning recognizable, and the rulebook that keeps it consistent.',
    deliverables: [
      'Primary logo, secondary lockups, and icon mark',
      'Color architecture (primary, secondary, functional)',
      'Typography system with web and desktop fonts',
      'Brand guidelines document (PDF, 30–50 pages)',
    ],
  },
  'website-growth': {
    subtitle: 'Website & Growth Systems',
    schedule: '6–8 weeks',
    summary: 'A positioning-first website, and the system behind it that captures and follows up on leads.',
    // Website Design is quoted with copywriting "for up to 10 core pages". The
    // review compares a site's page count against this figure; it was 7 under
    // the ICON Build.
    websitePages: 10,
    deliverables: [
      'Sitemap and page-level content strategy',
      'Copywriting for up to 10 core pages',
      'Lead capture forms connected to CRM or email',
      'Launch QA, go-live, and 30 days of post-launch support',
    ],
  },
  'marketing-leadership': {
    subtitle: 'Ongoing Leadership',
    schedule: 'Monthly',
    summary: 'Senior leadership and ongoing care that keep the system accurate and accountable.',
    deliverables: [
      '90-day marketing plan, refreshed quarterly',
      'Monthly pipeline and performance report',
      'Quarterly brand consistency audit',
    ],
  },
}
